import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";
import { Copy, RefreshCw, Upload, MessageCircle } from "lucide-react";

const WHATSAPP_NUMBER = "351910248486";

const METHOD_LABEL = { mbway: "MBWay", paypal: "PayPal", transfer: "Transferência Bancária", crypto: "Crypto" };

export default function Payment() {
  const { orderId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [settings, setSettings] = useState(null);
  const [polling, setPolling] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const [o, s] = await Promise.all([
        api.get(`/orders/${orderId}`),
        api.get("/settings/public"),
      ]);
      setOrder(o.data);
      setSettings(s.data);
      if (o.data.status === "paid" && !o.data.cards_drawn) navigate("/sorteio");
    } catch (e) {
      toast.error("Erro a carregar pedido");
    }
  };

  useEffect(() => { if (user) load(); }, [orderId, user]);
  useEffect(() => {
    if (!polling) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [polling, orderId]);

  if (!user) return <main className="pt-40 text-center"><p>Inicia sessão para ver este pedido.</p></main>;
  if (!order || !settings) return <main className="pt-40 text-center text-gold">A carregar…</main>;

  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success("Copiado"); };

  const uploadProof = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/orders/${orderId}/proof`, form);
      toast.success("Comprovativo enviado! Aguarda a confirmação do admin.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro a enviar comprovativo");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <main className="pt-28 pb-20 max-w-3xl mx-auto px-6 md:px-12">
      <h1 className="font-display text-5xl mb-3">Pagamento</h1>
      <p className="text-[color:var(--text-2)] mb-8">Pedido <span className="text-gold">#{order.order_id}</span></p>

      <div className="glass p-8 mb-6">
        <div className="grid grid-cols-2 gap-6 mb-6">
          <div><div className="text-xs uppercase text-[color:var(--text-3)]">Cartas</div><div className="font-display text-3xl text-gold">{order.quantity}</div></div>
          <div><div className="text-xs uppercase text-[color:var(--text-3)]">Total</div><div className="font-display text-3xl text-gold">{order.total_eur.toFixed(2)}€</div></div>
          <div><div className="text-xs uppercase text-[color:var(--text-3)]">Método</div><div className="text-lg">{METHOD_LABEL[order.payment_method]}</div></div>
          <div><div className="text-xs uppercase text-[color:var(--text-3)]">Estado</div>
            <div className={`text-lg uppercase ${order.status==="paid"?"text-gold":order.status==="pending"?"text-[color:var(--text-2)]":"text-red-400"}`} data-testid="order-status">{order.status}</div>
          </div>
        </div>

        {order.status === "pending" && (
          <div className="border-t border-[color:var(--border)] pt-6">
            <h2 className="font-display text-2xl mb-3">Como pagar</h2>
            {order.payment_method === "mbway" && (
              <PayLine label="MBWay" value={settings.mbway_phone} onCopy={copy} testid="mbway-value" />
            )}
            {order.payment_method === "paypal" && (
              <PayLine label="PayPal" value={settings.paypal_email} onCopy={copy} testid="paypal-value" />
            )}
            {order.payment_method === "transfer" && (
              <PayLine label="IBAN" value={settings.iban} onCopy={copy} testid="iban-value" />
            )}
            {order.payment_method === "crypto" && Object.entries(settings.crypto_wallets || {}).map(([k, v]) => (
              <PayLine key={k} label={k} value={v} onCopy={copy} testid={`crypto-${k.toLowerCase()}-value`} />
            ))}
            <p className="text-[color:var(--text-3)] text-sm mt-6">
              Indica o teu nome e o nº do pedido na descrição: <span className="text-gold">#{order.order_id}</span>. Assim que o administrador confirmar o pagamento, este pedido fica disponível para revelares as cartas.
            </p>

            <div className="flex gap-3 mt-6 flex-wrap items-center">
              <button className="btn-secondary flex items-center gap-2" onClick={() => { setPolling(true); load(); }} data-testid="refresh-status">
                <RefreshCw size={16}/> Atualizar
              </button>
              <label className="btn-primary flex items-center gap-2 cursor-pointer" data-testid="upload-proof-label">
                <Upload size={16}/> {uploading ? "A enviar…" : (order.proof_file ? "Substituir comprovativo" : "Upload comprovativo")}
                <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden"
                  onChange={uploadProof} disabled={uploading} data-testid="upload-proof-input" />
              </label>
              {polling && <span className="text-xs text-gold self-center">A verificar a cada 5s…</span>}
            </div>
            {order.proof_file && (
              <>
                <p className="text-xs text-[color:var(--text-3)] mt-2">
                  Comprovativo enviado em {new Date(order.proof_uploaded_at).toLocaleString("pt-PT")}.
                </p>
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`Olá! Enviei o comprovativo do pedido #${order.order_id}.`)}`}
                  target="_blank" rel="noreferrer"
                  className="btn-secondary flex items-center gap-2 w-fit mt-3"
                  data-testid="whatsapp-contact-btn"
                >
                  <MessageCircle size={16}/> Falar no WhatsApp
                </a>
              </>
            )}
          </div>
        )}

        {order.status === "paid" && (
          <button onClick={() => navigate("/sorteio")} className="btn-primary w-full">Revelar as cartas</button>
        )}
      </div>
    </main>
  );
}

function PayLine({ label, value, onCopy, testid }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-md border border-[color:var(--border)] mb-2">
      <span className="text-gold font-semibold w-20">{label}</span>
      <span className="flex-1 font-mono text-sm break-all" data-testid={testid}>{value}</span>
      <button onClick={() => onCopy(value)} className="text-[color:var(--text-2)] hover:text-gold" title="Copiar"><Copy size={16}/></button>
    </div>
  );
}
